import { lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorBoundary from '../components/resilience/ErrorBoundary';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { DashboardLayout } from '../layout/DashboardLayout';
import NoSessionScreen from '../components/organisms/NoSessionScreen';
import { NotFoundPage } from '../pages/NotFoundPage';

import { getStoredSession, initializeSession } from '../utils/sessionManager';
import { hasAetherAccess, ENFORCE_APP_ACCESS, isSuperAdmin } from '../utils/appAccess';
import { readLastRoute } from '../utils/lastRoute';

const CompetitionsPage = lazy(() => import('../pages/Competition'));
const SportsPage = lazy(() => import('../pages/Sport'));
const SeasonPage = lazy(() => import('../pages/Season'));
const FixturePage = lazy(() => import('../pages/Fixture'));
const AllFixturesPage = lazy(() => import('../pages/AllFixtures'));
const TeamSetupPage = lazy(() =>
  import('../pages/TeamSetupPage').then((m) => ({ default: m.TeamSetupPage }))
);
const CollectionPage = lazy(() => import('../pages/Collection'));
const StandingsPage = lazy(() =>
  import('../pages/StandingsPage').then((m) => ({ default: m.StandingsPage }))
);
const MonitoringPage = lazy(() => import('../pages/MonitoringPage'));
const CustomStatsPage = lazy(() => import('../pages/CustomStats'));
const MyBookmarksPage = lazy(() => import('../pages/MyBookmarks'));
const FormationCollectionPage = lazy(() =>
  import('../pages/FormationCollectionPage').then((m) => ({ default: m.FormationCollectionPage }))
);

/** Route boundary keyed on pathname, so navigation clears a failed route. */
const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary variant="route" resetKey={location.pathname} label="This page">
      {children}
    </ErrorBoundary>
  );
};

/* Redirects for old scoped collection URLs — preserve competition/season as query params */
const RedirectCollection = () => {
  const { competitionId, seasonId, collectionName } = useParams();
  const existing = new URLSearchParams(window.location.search);
  if (competitionId) existing.set('competitionId', competitionId);
  if (seasonId) existing.set('seasonId', seasonId);
  const qs = existing.toString();
  return <Navigate to={`/collections/${collectionName}${qs ? `?${qs}` : ''}`} replace />;
};

/* Remount TeamSetupPage on a fixture SWITCH (key on the route :fixtureId) so all
   local lineup/dialog state re-initialises per fixture — no cross-fixture state
   bleed (stale duplicate-player prompt, lineup input buffers, editing flags).
   Keyed on fixtureId only; a resync / in-place update on the same fixture does
   NOT remount. */
const TeamSetupRoute = () => {
  const { fixtureId } = useParams();
  return <TeamSetupPage key={fixtureId} />;
};

const RouteFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      minHeight: 200,
    }}
  >
    <CircularProgress sx={{ color: '#45FFD5' }} />
  </Box>
);

/* ---------------------------------------
   Protected Route (Outlet-based)
--------------------------------------- */
const ProtectedRoute = () => {
  let session = getStoredSession();

  // Try initializing from URL if session missing
  if (!session?.orgId) {
    const urlSession = initializeSession(window.location.href);
    if (!urlSession?.orgId) {
      return <Navigate to="/" replace />;
    }
    session = urlSession;
  }

  // OPUS-901: soft app-access gate (apps.aether.access). Logs a would-deny but
  // never blocks while the role->permission map is finalised; flip
  // ENFORCE_APP_ACCESS to hard-block.
  const allowed = hasAetherAccess(session);
  if (!allowed && ENFORCE_APP_ACCESS) {
    return <Navigate to="/" replace />;
  }

  return <DashboardLayout />;
};

/* ---------------------------------------
   Super-admin-only route guard. The wildcard '*' permission (super_admin)
   is required; everyone else is redirected to /competitions.
--------------------------------------- */
const SuperAdminRoute = ({ children }) => {
  const session = getStoredSession();
  return isSuperAdmin(session) ? children : <Navigate to="/competitions" replace />;
};

/* ---------------------------------------
   App Routes
--------------------------------------- */
const AppRoutes = () => {
  const session = getStoredSession();

  // If session exists in URL but not storage yet
  if (!session?.orgId) {
    const urlSession = initializeSession(window.location.href);
    if (urlSession?.orgId) {
      // Resume where they were. Falls back to /competitions whenever the stored
      // route is stale, unknown, or belongs to a different user.
      return <Navigate to={readLastRoute(urlSession.userId)} replace />;
    }
  }

  return (
    // Route-tier boundary, keyed on pathname so navigating away RESETS it — a
    // broken page must not stay broken once you leave it, and must not take the
    // nav or any other route down with it. Wrapping Suspense too, since a failed
    // lazy-chunk fetch is a throw as well and previously white-screened the app.
    <RouteErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
          <Routes>
          {/* Protected routes with layout */}
          <Route element={<ProtectedRoute />}>
            <Route path="/competitions" element={<CompetitionsPage />} />
            <Route path="/sports" element={<SportsPage />} />
            <Route path="/seasons/:competitionId" element={<SeasonPage />} />
            <Route path="/fixtures/:competitionId/:seasonId" element={<FixturePage />} />
            {/* OPUS-1408: a top-level page reached from the sidebar, alongside
                Teams/Players/Venues, rather than a season tab. It reads a
                different collection from /fixtures, so it is not a second view of
                the same data. Competition and season are chosen on the page
                because the DWS endpoint rejects an unscoped request. */}
            <Route path="/all-fixtures" element={<AllFixturesPage />} />
            {/* The page briefly shipped as a season tab; keep old links working. */}
            <Route path="/all-fixtures/:competitionId/:seasonId" element={<Navigate to="/all-fixtures" replace />} />
            <Route
              path="/teamsetup/:competitionId/:seasonId/:fixtureId"
              element={<TeamSetupRoute />}
            />
            <Route path="/collections/:collectionName" element={<CollectionPage />} />
            {/* Redirects from old competition/season-scoped collection URLs */}
            <Route path="/collections/:competitionId/:collectionName" element={<RedirectCollection />} />
            <Route path="/collections/:competitionId/:seasonId/:collectionName" element={<RedirectCollection />} />
            <Route path="/standings/:competitionId/:seasonId" element={<StandingsPage />} />
            <Route path="/formations" element={<FormationCollectionPage />} />
            <Route path="/formations/:competitionId/:seasonId" element={<Navigate to="/formations" replace />} />
            <Route path="/monitoring" element={<MonitoringPage />} />
            <Route path="/my-watchlist" element={<MyBookmarksPage />} />
            {/* Super-admin only — custom derived-stat definitions */}
            <Route
              path="/custom-stats"
              element={
                <SuperAdminRoute>
                  <CustomStatsPage />
                </SuperAdminRoute>
              }
            />
          </Route>

          {/* Root */}
          <Route
            path="/"
            element={
              session?.orgId ? (
                <Navigate to={readLastRoute(session.userId)} replace />
              ) : (
                <NoSessionScreen />
              )
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
};

export default AppRoutes;
